import { AfterViewInit, Component, forwardRef, Input, OnInit, StaticProvider } from "@angular/core";
import { UntypedFormBuilder, NG_VALIDATORS, NG_VALUE_ACCESSOR } from "@angular/forms";
import { Gender, UserLogin } from "@main/users";
import { ControlWithFormGroupBase } from ".";
import { UserHelperService } from "@main/users/user-helper.service";
import { UserPatientValidators } from "./user-patient/validators";

const EditUserValueAccessor = <StaticProvider>{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => EditUserControlComponent),
    multi: true
};

const EditUserValidator = <StaticProvider>{
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => EditUserControlComponent),
    multi: true
};

@Component({
    selector: "edit-user-control",
    templateUrl: "./edit-user-control.component.html",
    providers: [EditUserValueAccessor, EditUserValidator]
})
export class EditUserControlComponent extends ControlWithFormGroupBase<Partial<UserLogin>> implements OnInit, AfterViewInit {
    private _registrationNumberRequired: boolean;

    @Input() showOtherInformation = true;
    @Input() set registrationNumberRequired(required: boolean) {
        this._registrationNumberRequired = required;
        if (this.registrationNumberCtrl) {
            this.registrationNumberCtrl.clearValidators();
            this.registrationNumberCtrl.addValidators(UserPatientValidators.registrationNumber(required));
            this.registrationNumberCtrl.updateValueAndValidity({ onlySelf: true });
        }
    }
    get registrationNumberRequired() {
        return this._registrationNumberRequired;
    }

    constructor(fb: UntypedFormBuilder, private userHelperService: UserHelperService) {
        super(fb);
    }

    ngOnInit(): void {
        this.setupForm();
    }

    ngAfterViewInit(): void {
        this.form.markAllAsTouched();
    }

    private setupForm() {
        const form = this.formBuilder.group({
            id: [null],
            userId: [null],
            title: [{ value: null, disabled: this.isGenericPatient }, UserPatientValidators.title],
            firstName: [{ value: null, disabled: this.isGenericPatient }, UserPatientValidators.firstName],
            lastName: [{ value: null, disabled: this.isGenericPatient }, UserPatientValidators.lastName],
            dateOfBirth: [{ value: null, disabled: this.isGenericPatient }, UserPatientValidators.dateOfBirth],
            gender: [{ value: Gender.NotApplicable, disabled: this.isGenericPatient }, UserPatientValidators.gender],
            phoneNumber: [{ value: null, disabled: this.isGenericPatient }, UserPatientValidators.phoneNumber],
            email: [{ value: null, disabled: this.isGenericPatient }, UserPatientValidators.email(true)],
            registrationNumber: [
                { value: null, disabled: this.isGenericPatient },
                UserPatientValidators.registrationNumber(this.registrationNumberRequired)
            ],
            addressLine1: [{ value: null, disabled: this.isGenericPatient }, UserPatientValidators.addressLine(false)],
            addressLine2: [{ value: null, disabled: this.isGenericPatient }, UserPatientValidators.addressLine(false)],
            addressLine3: [{ value: null, disabled: this.isGenericPatient }, UserPatientValidators.addressLine(false)],
            addressLine4: [{ value: null, disabled: this.isGenericPatient }, UserPatientValidators.addressLine(false)],
            postcode: [{ value: null, disabled: this.isGenericPatient }, UserPatientValidators.postcode(false)],
            /**
             * Leaving following controls/fields but please consider not adding new ones, instead try
             * to add in the form that uses this component
             */
            deliveryAddressLine1: [null],
            deliveryAddressLine2: [null],
            deliveryAddressLine3: [null],
            deliveryAddressLine4: [null],
            deliveryPostcode: [null],
            createdDateTime: [null]
        });
        this.setForm(form);
    }

    get errorKey(): string {
        return "editUser";
    }

    get registrationNumberCtrl() {
        return this.form.get("registrationNumber");
    }

    writeValue(obj: Partial<UserLogin>): void {
        super.writeValue(obj);
        this.registrationNumberRequired = obj?.registrationNumberInfo?.required;
    }

    private get isGenericPatient() {
        return this.userHelperService.isGenericPatient;
    }
}
